// ============================================
// ASSISTANCE REQUEST TYPES & CONFIGS
// ============================================

export type AssistanceCategory =
  | 'bug_fix'
  | 'content_update'
  | 'feature_request'
  | 'security'
  | 'performance'
  | 'design_change'
  | 'seo'
  | 'hosting'
  | 'training'
  | 'other'

export type AssistanceStatus =
  | 'submitted'
  | 'reviewing'
  | 'approved'
  | 'in_progress'
  | 'completed'
  | 'rejected'
  | 'billed'

export type AssistancePriority = 'low' | 'medium' | 'high' | 'urgent'

export type ContractStatus = 'active' | 'paused' | 'cancelled' | 'expired'
export type BillingFrequency = 'monthly' | 'quarterly' | 'yearly'

// ============================================
// LABELS FR
// ============================================

export const ASSISTANCE_CATEGORY_LABELS: Record<AssistanceCategory, string> = {
  bug_fix: 'Correction de bug',
  content_update: 'Mise à jour contenu',
  feature_request: 'Nouvelle fonctionnalité',
  security: 'Sécurité',
  performance: 'Performance',
  design_change: 'Modification design',
  seo: 'SEO',
  hosting: 'Hébergement',
  training: 'Formation',
  other: 'Autre',
}

export const ASSISTANCE_STATUS_LABELS: Record<AssistanceStatus, string> = {
  submitted: 'Soumise',
  reviewing: 'En examen',
  approved: 'Approuvée',
  in_progress: 'En cours',
  completed: 'Terminée',
  rejected: 'Refusée',
  billed: 'Facturée',
}

export const PRIORITY_LABELS: Record<AssistancePriority, string> = {
  low: 'Basse',
  medium: 'Moyenne',
  high: 'Haute',
  urgent: 'Urgente',
}

export const CONTRACT_STATUS_LABELS: Record<ContractStatus, string> = {
  active: 'Actif',
  paused: 'En pause',
  cancelled: 'Résilié',
  expired: 'Expiré',
}

export const BILLING_FREQUENCY_LABELS: Record<BillingFrequency, string> = {
  monthly: 'Mensuel',
  quarterly: 'Trimestriel',
  yearly: 'Annuel',
}

// ============================================
// BADGE CONFIGS
// ============================================

import type { BadgeProps } from '@/components/ui/Badge'

export const ASSISTANCE_STATUS_CONFIG: Record<AssistanceStatus, { label: string; color: BadgeProps['variant'] }> = {
  submitted: { label: 'Soumise', color: 'gray' },
  reviewing: { label: 'En examen', color: 'blue' },
  approved: { label: 'Approuvée', color: 'mint' },
  in_progress: { label: 'En cours', color: 'gold' },
  completed: { label: 'Terminée', color: 'mint' },
  rejected: { label: 'Refusée', color: 'red' },
  billed: { label: 'Facturée', color: 'brown' },
}

export const PRIORITY_CONFIG: Record<AssistancePriority, { label: string; color: BadgeProps['variant'] }> = {
  low: { label: 'Basse', color: 'gray' },
  medium: { label: 'Moyenne', color: 'blue' },
  high: { label: 'Haute', color: 'gold' },
  urgent: { label: 'Urgente', color: 'red' },
}

export const CONTRACT_STATUS_CONFIG: Record<ContractStatus, { label: string; color: BadgeProps['variant'] }> = {
  active: { label: 'Actif', color: 'mint' },
  paused: { label: 'En pause', color: 'gold' },
  cancelled: { label: 'Résilié', color: 'red' },
  expired: { label: 'Expiré', color: 'gray' },
}

// ============================================
// ROW TYPES
// ============================================

export interface AssistanceRequestRow {
  id: string
  project_id: string
  contract_id: string | null
  user_id: string
  title: string
  description: string
  category: AssistanceCategory
  priority: AssistancePriority
  is_covered: boolean | null
  estimated_hours: number | null
  actual_hours: number | null
  billing_notes: string | null
  estimated_cost: number | null
  status: AssistanceStatus
  assigned_to: string | null
  resolution_notes: string | null
  resolved_at: string | null
  created_at: string
  updated_at: string
}

export interface AssistanceRequestWithDetails extends AssistanceRequestRow {
  project?: {
    id: string
    title: string
    user_id: string
  } | null
  user?: {
    id: string
    full_name: string | null
    email: string
  } | null
  assigned_admin?: {
    id: string
    full_name: string | null
    email: string
  } | null
}

export interface ContractRow {
  id: string
  client_id: string
  project_id: string | null
  title: string
  description: string | null
  type: string | null
  status: ContractStatus
  amount: number
  currency: string
  billing_frequency: BillingFrequency
  start_date: string
  end_date: string | null
  next_billing_date: string | null
  auto_renew: boolean
  terms: string | null
  signed_at: string | null
  cancelled_at: string | null
  cancellation_reason: string | null
  created_at: string
  updated_at: string
}

export interface ContractWithCoverages extends ContractRow {
  contract_coverages?: ContractCoverageRow[]
}

export interface ContractCoverageRow {
  id: string
  contract_id: string
  coverage_type: string
  label: string
  description: string | null
  is_covered: boolean
  max_hours_per_month: number | null
  hourly_rate_if_exceeded: number | null
  created_at: string
  updated_at: string
}

export interface AssistanceFilters {
  status?: string
  priority?: string
  search?: string
  projectId?: string
  isCovered?: string
}

// ============================================
// DEFAULT COVERAGE TYPES
// ============================================

export const VALID_COVERAGE_TYPES = [
  'bug_fix',
  'security_update',
  'content_update',
  'new_feature',
  'performance',
  'hosting',
  'backup',
  'monitoring',
  'seo',
  'other',
] as const

export type CoverageType = typeof VALID_COVERAGE_TYPES[number]

export const COVERAGE_TYPE_LABELS: Record<CoverageType, string> = {
  bug_fix: 'Correction de bugs',
  security_update: 'Mises à jour de sécurité',
  content_update: 'Mise à jour de contenu',
  new_feature: 'Nouvelle fonctionnalité',
  performance: 'Optimisation performance',
  hosting: 'Hébergement & serveur',
  backup: 'Sauvegardes',
  monitoring: 'Monitoring & alertes',
  seo: 'Maintenance SEO',
  other: 'Autre',
}

export const DEFAULT_COVERAGE_TYPES: { coverage_type: CoverageType; label: string }[] = [
  { coverage_type: 'bug_fix', label: 'Correction de bugs' },
  { coverage_type: 'content_update', label: 'Mise à jour de contenu' },
  { coverage_type: 'security_update', label: 'Mises à jour de sécurité' },
  { coverage_type: 'performance', label: 'Optimisation performance' },
  { coverage_type: 'hosting', label: 'Hébergement & serveur' },
  { coverage_type: 'backup', label: 'Sauvegardes' },
  { coverage_type: 'seo', label: 'Maintenance SEO' },
  { coverage_type: 'monitoring', label: 'Monitoring & alertes' },
  { coverage_type: 'new_feature', label: 'Nouvelle fonctionnalité' },
  { coverage_type: 'other', label: 'Autre' },
]
